import React, { useEffect, useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Card,
  CardContent,
  CardActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
} from '@material-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { partsApi, Part } from '../../services/partsApi';
import { 
  fetchPartsStart, 
  addPart, 
  updatePart, 
  setSelectedPart,
  clearError 
} from '../../store/reducers/partsSlice';
import { downloadDocument } from '../../utils/documentDownload';
import SerialImport from '../Common/SerialImport';

interface Document {
  id: string;
  Name: string;
  DocumentNum: string;
  Description: string;
  Version: string;
}

interface Serial {
  id: string;
  SerialNum: string;
  assigned: boolean;
}

const PartsEdit: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { selectedPart, loading, error } = useSelector((state: RootState) => state.parts);
  
  const [part, setPart] = useState<Part>({
    part_num: '',
    description: '',
    part_issue: '',
    client_id: '',
    part_classification_id: '',
    price_per_unit: 0,
    is_active: true,
    Documents: [],
    PartSerials: [],
  });
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [documentLoading, setDocumentLoading] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (id && id !== 'new') {
      setEditMode(true);
      loadPartData(id);
    } else {
      setEditMode(false);
      setPart({
        part_num: '',
        description: '',
        part_issue: '',
        client_id: '',
        part_classification_id: '',
        price_per_unit: 0,
        is_active: true,
        Documents: [],
        PartSerials: [],
      });
    }
  }, [id]);

  const loadPartData = async (partId: string) => {
    try {
      const partData = await partsApi.getById(partId);
      setPart(partData);
      dispatch(setSelectedPart(partData));
    } catch (error) {
      console.error('Error loading part:', error);
      setValidationErrors(['Failed to load part data. Please try again.']);
    }
  };

  const handleInputChange = (field: keyof Part, value: any) => {
    setPart(prev => ({ ...prev, [field]: value }));
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const validatePart = (partData: Part): string[] => {
    const errors: string[] = [];
    
    // Required fields validation (matching backend schema)
    if (!partData.part_num?.trim()) {
      errors.push('Part Number is required');
    } else if (partData.part_num.length > 50) {
      errors.push('Part Number must be 50 characters or less');
    }
    
    if (!partData.description?.trim()) {
      errors.push('Description is required');
    } else if (partData.description.length > 150) {
      errors.push('Description must be 150 characters or less');
    }
    
    if (!partData.client_id?.trim()) {
      errors.push('Client is required');
    } else if (isNaN(parseInt(partData.client_id))) {
      errors.push('Client ID must be a valid number');
    }
    
    if (!partData.part_classification_id?.trim()) {
      errors.push('Part Classification is required');
    } else if (isNaN(parseInt(partData.part_classification_id))) {
      errors.push('Part Classification ID must be a valid number');
    }
    
    if (!partData.part_issue?.trim()) {
      errors.push('Issue is required');
    } else if (partData.part_issue.length > 50) {
      errors.push('Issue must be 50 characters or less');
    }
    
    if (partData.price_per_unit < 0) {
      errors.push('Price per unit cannot be negative');
    }
    
    // Validate serial numbers (backend checks for duplicates)
    if (partData.PartSerials && partData.PartSerials.length > 0) {
      const serialNumbers = partData.PartSerials.map(s => s.SerialNum.trim()).filter(s => s);
      const uniqueSerials = new Set(serialNumbers);
      if (serialNumbers.length !== uniqueSerials.size) {
        errors.push('Duplicate serial numbers are not allowed');
      }
    }
    
    return errors;
  };

  const handleSave = async () => {
    setValidationErrors([]);
    
    // Validate the part data
    const errors = validatePart(part);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSaving(true);
    try {
      if (editMode) {
        // Update existing part
        await partsApi.update(part.id!, part);
        dispatch(updatePart(part));
      } else {
        // Create new part
        const newPart = await partsApi.create(part);
        dispatch(addPart(newPart));
      }
      
      navigate('/parts');
    } catch (error: any) {
      console.error('Error saving part:', error);
      setValidationErrors([error.message || 'Failed to save part. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/parts');
  };

  const handleDownloadDocument = async (document: Document) => {
    setDocumentLoading(document.id);
    try {
      await downloadDocument(document.id);
    } catch (error) {
      console.error('Error downloading document:', error);
      setValidationErrors(['Failed to download document. Please try again.']);
    } finally {
      setDocumentLoading(null);
    }
  };

  const handleDeleteDocument = (documentId: string) => {
    setPart(prev => ({
      ...prev,
      Documents: prev.Documents?.filter(doc => doc.id !== documentId) || [],
    }));
  };

  const handleSerialsChange = (newSerials: { SerialNum: string }[]) => {
    // Convert to the format expected by the Part interface
    const formattedSerials = newSerials.map((serial, index) => ({
      id: serial.SerialNum, // Use SerialNum as ID for new serials
      SerialNum: serial.SerialNum,
      assigned: false, // New serials are not assigned by default
    }));

    setPart(prev => ({
      ...prev,
      PartSerials: formattedSerials,
    }));
  };

  const handleErrorClose = () => {
    setValidationErrors([]);
    dispatch(clearError());
  };

  // Convert PartSerials to the format expected by SerialImport
  const serialsForImport = part.PartSerials?.map(serial => ({
    SerialNum: serial.SerialNum,
  })) || [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {editMode ? 'Edit Part' : 'Add New Part'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBack}
        >
          Back to Parts
        </Button>
      </Box>

      {/* Error handling */}
      {(error || validationErrors.length > 0) && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={handleErrorClose}
        >
          <Alert onClose={handleErrorClose} severity="error">
            {error || validationErrors.join(', ')}
          </Alert>
        </Snackbar>
      )}

      {/* Loading state */}
      {loading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      <Paper>
        <Box p={3}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Part Number"
                value={part.part_num}
                onChange={(e) => handleInputChange('part_num', e.target.value)}
                disabled={editMode}
                margin="normal"
                required
                inputProps={{ maxLength: 50 }}
                helperText={`${part.part_num?.length || 0}/50 characters`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Description"
                value={part.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                margin="normal"
                required
                inputProps={{ maxLength: 150 }}
                helperText={`${part.description?.length || 0}/150 characters`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Issue"
                value={part.part_issue}
                onChange={(e) => handleInputChange('part_issue', e.target.value)}
                margin="normal"
                required
                inputProps={{ maxLength: 50 }}
                helperText={`${part.part_issue?.length || 0}/50 characters`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Client</InputLabel>
                <Select
                  value={part.client_id}
                  onChange={(e) => handleInputChange('client_id', e.target.value)}
                >
                  <MenuItem value="CLIENT-001">Client 001</MenuItem>
                  <MenuItem value="CLIENT-002">Client 002</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Classification</InputLabel>
                <Select
                  value={part.part_classification_id}
                  onChange={(e) => handleInputChange('part_classification_id', e.target.value)}
                >
                  <MenuItem value="CLASS-A">Class A</MenuItem>
                  <MenuItem value="CLASS-B">Class B</MenuItem>
                  <MenuItem value="CLASS-C">Class C</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price Per Unit"
                type="number"
                value={part.price_per_unit || ''}
                onChange={(e) => handleInputChange('price_per_unit', parseFloat(e.target.value) || 0)}
                margin="normal"
                required
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Enter price in decimal format (e.g., 100.50)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={part.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: '2rem 0' }} />

          {/* Documents Section */}
          <Typography variant="h6" gutterBottom>
            Documents
          </Typography>
          <Card variant="outlined" style={{ marginBottom: '1rem' }}>
            <CardContent>
              <Typography variant="body2" color="textSecondary">
                Add new documents or view existing ones
              </Typography>
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<AddIcon />}>
                Add Document
              </Button>
            </CardActions>
          </Card>

          {part.Documents && part.Documents.length > 0 && (
            <List>
              {part.Documents.map((doc) => (
                <ListItem key={doc.id}>
                  <ListItemText
                    primary={doc.Name}
                    secondary={`${doc.DocumentNum} - Version ${doc.Version}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleDownloadDocument(doc)}
                      disabled={documentLoading === doc.id}
                      title="Download Document"
                    >
                      {documentLoading === doc.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DownloadIcon />
                      )}
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteDocument(doc.id)}
                      title="Delete Document"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <Divider style={{ margin: '2rem 0' }} />

          {/* Serial Numbers Section */}
          <Typography variant="h6" gutterBottom>
            Serial Numbers
          </Typography>
          <SerialImport
            value={serialsForImport}
            onChange={handleSerialsChange}
            placeholder="Enter serial number and press Enter or Space"
            disabled={loading}
          />

          {/* Display existing serials that are assigned (read-only) */}
          {part.PartSerials && part.PartSerials.some(serial => serial.assigned) && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Assigned Serial Numbers:
              </Typography>
              <List dense>
                {part.PartSerials.filter(serial => serial.assigned).map((serial) => (
                  <ListItem key={serial.id}>
                    <ListItemText
                      primary={serial.SerialNum}
                      secondary="Assigned (cannot be modified)"
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>

        <Box p={3} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handleBack}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PartsEdit; 